/**
 * Сортировка таблицы «Эффективность операторов».
 *
 * Сортируется каждый столбец `buildEfficiencyColumns`, в обе стороны. Состояние — `{key,
 * direction}`, где `key` — ключ столбца (`stage:{code}` у этапов воронки). Выбор переживает уход
 * из раздела (`config/employeeEfficiencySortState.js`), поэтому сохранённый ключ сверяется с
 * текущими столбцами: этап, которого нет в новом пакете узлов, возвращает умолчание.
 *
 * Конверсия и чек-лист сортируются по «ярусам»: сначала строки с достаточными данными, затем с
 * недостаточными, в конце — без значения, и так в обоих направлениях. Иначе при сортировке по
 * возрастанию «худшими» вставали бы операторы с 0% на двух звонках — те самые, которых таблица
 * помечает «Недостаточно данных» и не сравнивает с остальными.
 */
import {
  hasEnoughChecklistData,
  hasEnoughConversionData,
  readCallCount,
  readComplaintCount,
  readFiniteNumber,
  readFunnelStages,
  readStageCount
} from './employeeEfficiency';

/** Прежний фиксированный порядок таблицы — он же умолчание. */
export const DEFAULT_EFFICIENCY_SORT = Object.freeze({ key: 'calls', direction: 'desc' });

const STAGE_KEY_PREFIX = 'stage:';

const isSortDirection = (value) => value === 'asc' || value === 'desc';

/**
 * Действующая сортировка: сохранённая, если её столбец есть в таблице, иначе умолчание.
 * `columns` — результат `buildEfficiencyColumns`.
 */
export const resolveEfficiencySort = (saved, columns) => {
  const known = Array.isArray(columns) && columns.some((column) => column.key === saved?.key);

  return known && isSortDirection(saved.direction)
    ? { key: saved.key, direction: saved.direction }
    : DEFAULT_EFFICIENCY_SORT;
};

/**
 * Клик по заголовку: тот же столбец переворачивает направление, новый начинается со своего
 * `firstSortDirection` (по умолчанию — по убыванию). Состояния «без сортировки» нет, как и в
 * журнале звонков: вернуть прежний порядок — это клик по «Звонкам».
 */
export const toggleEfficiencySort = (current, key, columns) => {
  if (current?.key === key) {
    return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  }

  const column = Array.isArray(columns) ? columns.find((candidate) => candidate.key === key) : null;
  return { key, direction: column?.firstSortDirection ?? 'desc' };
};

/** Значение `aria-sort` заголовка: только у активного столбца, у остальных атрибута нет. */
export const ariaSortFor = (sort, key) => {
  if (sort?.key !== key) {
    return undefined;
  }

  return sort.direction === 'asc' ? 'ascending' : 'descending';
};

const TIER_ENOUGH = 0;
const TIER_INSUFFICIENT = 1;
const TIER_EMPTY = 2;

const nameCollator = new Intl.Collator('ru', { numeric: true, sensitivity: 'base' });

const readName = (row) => (typeof row?.employeeName === 'string' ? row.employeeName.trim() : '');

const counted = (value) => ({ value, tier: TIER_ENOUGH });

const ranked = (raw, enough) => {
  const value = readFiniteNumber(raw);

  if (value === null) {
    return { value, tier: TIER_EMPTY };
  }

  return { value, tier: enough ? TIER_ENOUGH : TIER_INSUFFICIENT };
};

const SORT_VALUE_READERS = {
  employee: (row) => {
    const name = readName(row);
    return { value: name, tier: name ? TIER_ENOUGH : TIER_EMPTY };
  },
  calls: (row) => counted(readCallCount(row)),
  complaints: (row) => counted(readComplaintCount(row)),
  conversion: (row) => ranked(row?.conversionRate, hasEnoughConversionData(row)),
  checklist: (row) => ranked(row?.avgFirstOperatorTotal, hasEnoughChecklistData(row))
};

const resolveSortValueReader = (key, stages) => {
  if (typeof key === 'string' && key.startsWith(STAGE_KEY_PREFIX)) {
    const code = key.slice(STAGE_KEY_PREFIX.length);
    const index = readFunnelStages({ stages }).findIndex((stage) => stage.code === code);
    return (row) => counted(index >= 0 ? readStageCount(row, index) : 0);
  }

  return SORT_VALUE_READERS[key] ?? SORT_VALUE_READERS[DEFAULT_EFFICIENCY_SORT.key];
};

const compareValues = (left, right) => {
  if (typeof left === 'string' || typeof right === 'string') {
    return nameCollator.compare(String(left ?? ''), String(right ?? ''));
  }

  return (left ?? 0) - (right ?? 0);
};

// Равные строки — по имени, затем по id: порядок не прыгает между опросами таблицы.
const compareRowsStably = (left, right) => {
  const leftName = readName(left);
  const rightName = readName(right);

  if (leftName !== rightName) {
    if (!leftName) return 1;
    if (!rightName) return -1;
    const byName = nameCollator.compare(leftName, rightName);
    if (byName !== 0) return byName;
  }

  return String(left?.employeeId ?? '').localeCompare(String(right?.employeeId ?? ''));
};

/**
 * Строки в порядке сортировки. Возвращает копию — массив состояния компонента не меняется.
 * `stages` — этапы воронки ответа: по ним ключ `stage:{code}` находит индекс в `stageCounts`.
 */
export const sortEfficiencyRows = (rows, sort, stages) => {
  const list = Array.isArray(rows) ? rows : [];
  const { key, direction } = sort ?? DEFAULT_EFFICIENCY_SORT;
  const readValue = resolveSortValueReader(key, stages);
  const sign = direction === 'asc' ? 1 : -1;

  return list
    .map((row) => ({ row, ...readValue(row) }))
    .sort(
      (left, right) =>
        left.tier - right.tier ||
        sign * compareValues(left.value, right.value) ||
        compareRowsStably(left.row, right.row)
    )
    .map((entry) => entry.row);
};
